import { ICashlessRepository } from "domains/cashless/interfaces/ICashlessRepository";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { IOrderRepository } from "domains/order/interfaces/repositories/IOrderRepository";
import { INfceService } from "modules/order/application/interfaces/INfceService";
import { EmitNfceUseCase } from "modules/order/application/useCases/EmitNfceUseCase";
import { shouldEmitNfce } from "modules/order/application/useCases/shouldEmitNfce";
import { SyncOrderUseCase } from "modules/order/application/useCases/SyncOrderUseCase";
import { IOrder } from "modules/order/domain/models/IOrder";
import { Payment } from "modules/order/domain/models/Payment";
import { PixPaymentResponse } from "modules/payment/domain/models/PixApprovedTransaction";
import { TransactionType } from "modules/payment/domain/models/TransactionType";
import {
	PENDING_PIX_RESEND_WINDOW_MS,
	PENDING_PIX_RETENTION_MS,
	PENDING_PIX_VERIFY_WINDOW_MS,
} from "./pendingPixPolicy";
import { getPendingPixList, IPendingPix, isPixActive, prunePendingPix, updatePendingPix } from "./pendingPixQueue";

export interface IPendingPixDeps {
	getPixStatus(pixId: string): Promise<PixPaymentResponse | null>; // já normalizado
	orderRepository: IOrderRepository;
	cashlessRepository: ICashlessRepository;
	nfceService: INfceService;
	addOrderLog(order: IOrder, message: string, pixId?: string): Promise<void>;
	now(): number;
}

export const RESEND_DEADLINE_ERROR = "Prazo de reenvio automático esgotado";
export const OTHER_LOCAL_ERROR = "Totem em outro local";
const SYNC_FALLBACK_ERROR = "Falha ao sincronizar pagamento";

let processing = false;

const errorMessage = (error: unknown, fallback: string): string =>
	error instanceof Error ? error.message : typeof error === "string" ? error : fallback;

// O log do pedido nunca pode interromper o verificador
const log = async (deps: IPendingPixDeps, item: IPendingPix, message: string) => {
	try {
		await deps.addOrderLog(item.order, message, item.pixId);
	} catch {
		// ignora
	}
};

const latest = (item: IPendingPix): IPendingPix | undefined =>
	getPendingPixList().find((it) => it.pixId === item.pixId);

const transition = async (
	deps: IPendingPixDeps,
	item: IPendingPix,
	patch: Partial<IPendingPix>,
	message: string
): Promise<IPendingPix> => {
	const updated = updatePendingPix(item.pixId, patch) ?? { ...item, ...patch };
	await log(deps, updated, message);
	return updated;
};

const emitNfce = async (deps: IPendingPixDeps, item: IPendingPix, order: IOrder): Promise<IPendingPix> => {
	try {
		const nfce = await EmitNfceUseCase(deps.nfceService, order);
		// Mesma regra da tela: array vazio é falha
		if (!nfce || nfce.length === 0) {
			return transition(
				deps,
				item,
				{ nfceStatus: "failed", nfcePending: false },
				`Falha ao emitir NFC-e do PIX ${item.pixId}: resposta vazia`
			);
		}
		return transition(deps, item, { nfceStatus: "emitted", nfcePending: false }, `NFC-e do PIX ${item.pixId} emitida`);
	} catch (error) {
		return transition(
			deps,
			item,
			{ nfceStatus: "failed", nfcePending: false },
			`Falha ao emitir NFC-e do PIX ${item.pixId}: ${errorMessage(error, "erro desconhecido")}`
		);
	}
};

export const SendPendingPixUseCase = async (
	item: IPendingPix,
	deps: IPendingPixDeps,
	kiosk: Kiosk
): Promise<IPendingPix> => {
	const current = latest(item) ?? item;
	// Já enviado: nunca reenviar (evita crédito em dobro)
	if (current.status === "sent") {
		return current;
	}
	const pix = current.pix;
	if (!pix) {
		// Sem aprovação registrada não há o que enviar
		return current;
	}
	if (current.localId !== kiosk.localId) {
		return transition(
			deps,
			current,
			{ status: "send_failed", lastError: OTHER_LOCAL_ERROR },
			`PIX ${current.pixId} não enviado: ${OTHER_LOCAL_ERROR}`
		);
	}
	if (pix.value !== current.order.totalValue) {
		await log(
			deps,
			current,
			`PIX aprovado com valor diferente do pedido: pedido ${current.order.totalValue}, PIX ${pix.value}`
		);
	}

	const order: IOrder = {
		...current.order,
		paymentPix: pix,
		payment: {
			success: true,
			amount: pix.value,
			paymentMethod: TransactionType.pix,
			FinancialTransactionModel: { TipoAdquirente: 10 }, // meep
		} as Payment,
	};

	let success = false;
	let failure = SYNC_FALLBACK_ERROR;
	try {
		const result = await SyncOrderUseCase(
			deps.orderRepository,
			deps.cashlessRepository,
			order,
			TransactionType.pix,
			kiosk,
			current.kind
		);
		success = !!result?.success;
		failure = result?.message || SYNC_FALLBACK_ERROR;
	} catch (error) {
		failure = errorMessage(error, SYNC_FALLBACK_ERROR);
	}

	if (!success) {
		return transition(
			deps,
			current,
			{ status: "send_failed", sendAttempts: current.sendAttempts + 1, lastError: failure },
			`PIX ${current.pixId} falha no envio: ${failure}`
		);
	}

	const sent = await transition(
		deps,
		current,
		{ status: "sent", sendAttempts: current.sendAttempts + 1, lastError: undefined },
		`PIX ${current.pixId} enviado`
	);
	// Aprovação vista pela tela emite a nota no onSucessPayment, exceto quando o envio da tela falhou (nfcePending)
	if (
		(sent.approvedBy === "background" || sent.nfcePending) &&
		sent.nfceStatus !== "emitted" &&
		shouldEmitNfce(kiosk, sent.kind, TransactionType.pix)
	) {
		return emitNfce(deps, sent, order);
	}
	return sent;
};

const sendOrGiveUp = async (item: IPendingPix, deps: IPendingPixDeps, kiosk: Kiosk, now: number) => {
	if (now - item.createdAt <= PENDING_PIX_RESEND_WINDOW_MS) {
		await SendPendingPixUseCase(item, deps, kiosk);
		return;
	}
	if (item.status === "send_failed" && item.lastError === RESEND_DEADLINE_ERROR) {
		return;
	}
	await transition(
		deps,
		item,
		{ status: "send_failed", lastError: RESEND_DEADLINE_ERROR },
		`PIX ${item.pixId}: ${RESEND_DEADLINE_ERROR}`
	);
};

const NO_ANSWER_ERROR = "Sem resposta do servidor";

const checkWaiting = async (item: IPendingPix, deps: IPendingPixDeps, kiosk: Kiosk, now: number) => {
	// Sem resposta real até a retenção: para de consultar (a aba mostra "Sem confirmação")
	if (now - item.createdAt > PENDING_PIX_RETENTION_MS) {
		return;
	}
	const checkAttempts = item.checkAttempts + 1;
	let response: PixPaymentResponse | null = null;
	let checkError: string | undefined;
	try {
		response = await deps.getPixStatus(item.pixId);
	} catch (error) {
		checkError = errorMessage(error, NO_ANSWER_ERROR);
	}

	// Só uma resposta real sobre este PIX decide; erro nunca expira (PIX pago não pode se perder)
	if (!response || response.paymentId !== item.pixId) {
		updatePendingPix(item.pixId, { checkAttempts, lastCheckError: checkError ?? NO_ANSWER_ERROR });
		return;
	}

	if (response.status === "approved") {
		const sending = await transition(
			deps,
			item,
			{
				status: "sending",
				pix: response,
				approvedAt: now,
				approvedBy: "background",
				checkAttempts,
				lastCheckError: undefined,
			},
			`PIX ${item.pixId} aprovado (verificador em segundo plano)`
		);
		await sendOrGiveUp(sending, deps, kiosk, now);
		return;
	}

	if (response.status === "cancelled") {
		await transition(
			deps,
			item,
			{ status: "expired", checkAttempts, lastCheckError: undefined },
			`PIX ${item.pixId} cancelado`
		);
		return;
	}

	if (now - item.createdAt > PENDING_PIX_VERIFY_WINDOW_MS) {
		await transition(
			deps,
			item,
			{ status: "expired", checkAttempts, lastCheckError: undefined },
			`PIX ${item.pixId} expirado sem pagamento`
		);
		return;
	}
	updatePendingPix(item.pixId, { checkAttempts, lastCheckError: undefined });
};

const processItem = async (item: IPendingPix, deps: IPendingPixDeps, kiosk: Kiosk, now: number) => {
	switch (item.status) {
		case "waiting":
			return checkWaiting(item, deps, kiosk, now);
		case "sending":
		case "send_failed":
			return sendOrGiveUp(item, deps, kiosk, now);
		default:
			return;
	}
};

export const ProcessPendingPixUseCase = async (deps: IPendingPixDeps, kiosk: Kiosk): Promise<void> => {
	if (processing) {
		return;
	}
	processing = true;
	try {
		const now = deps.now();
		for (const snapshot of getPendingPixList()) {
			const item = latest(snapshot);
			if (!item || isPixActive(item.pixId)) {
				continue;
			}
			try {
				await processItem(item, deps, kiosk, now);
			} catch (error) {
				try {
					updatePendingPix(item.pixId, { lastError: errorMessage(error, "Erro ao processar PIX") });
				} catch {
					// ignora
				}
			}
		}
		prunePendingPix(now);
	} finally {
		processing = false;
	}
};
